/* eslint-disable */

import React, { useState } from 'react'
import { Box, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import ColorLensIcon from '@mui/icons-material/ColorLens'
import ColorPicker from './ColorPicker'
import ColorwayDot from './ColorwayDot'

export interface ColorwayFormData {
  name: string
  hex_color: string
  hex_color_locked: boolean
  hex_colors?: string[]
}

interface ColorwayFormDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: () => void
  onBack?: () => void
  formData: ColorwayFormData
  onChange: (data: ColorwayFormData) => void
  buttonText?: string
}

function getHexColors(formData: ColorwayFormData): string[] {
  if (formData.hex_colors && formData.hex_colors.length > 0) {
    return formData.hex_colors
  }
  return formData.hex_color ? [formData.hex_color] : ['#000000']
}

export default function ColorwayFormDialog({ open, onClose, onSubmit, onBack, formData, onChange, buttonText }: ColorwayFormDialogProps) {
  const hexColors = getHexColors(formData)
  const [pickingForIndex, setPickingForIndex] = useState(0)

  const handleColorChange = (index: number, hexColor: string) => {
    const next = [...hexColors]
    next[index] = hexColor
    onChange({ ...formData, hex_colors: next, hex_color: next[0] })
  }

  const handleAddColor = () => {
    const next = [...hexColors, '#808080']
    onChange({ ...formData, hex_colors: next, hex_color: hexColors[0] })
    setPickingForIndex(next.length - 1)
  }

  const handleRemoveColor = (index: number) => {
    if (hexColors.length <= 1) return
    const next = hexColors.filter((_, i) => i !== index)
    onChange({ ...formData, hex_colors: next, hex_color: next[0] })
    if (pickingForIndex >= next.length) setPickingForIndex(next.length - 1)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add Colorway</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <TextField
            label="Colorway Name"
            fullWidth
            value={formData.name}
            onChange={(e) => onChange({ ...formData, name: e.target.value })}
            sx={{ mb: 2 }}
            required
          />

          <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Colors list - compact, next to photo */}
            <Box sx={{ flex: '0 0 auto', minWidth: 200 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>Colors</Typography>
              {hexColors.map((hexColor, index) => (
                <Box
                  key={index}
                  onClick={() => setPickingForIndex(index)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 1,
                    p: 1,
                    borderRadius: 1,
                    border: '2px solid',
                    borderColor: pickingForIndex === index ? 'primary.main' : 'transparent',
                    bgcolor: pickingForIndex === index ? 'action.selected' : 'transparent',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <Box sx={{ flexShrink: 0, overflow: 'hidden', borderRadius: 1 }}>
                    <input
                      type="color"
                      value={hexColor}
                      onChange={(e) => handleColorChange(index, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: 36,
                        height: 36,
                        padding: 0,
                        border: '1px solid rgba(0,0,0,0.23)',
                        borderRadius: 4,
                        cursor: 'pointer',
                        backgroundColor: 'transparent'
                      }}
                    />
                  </Box>
                  <TextField
                    value={hexColor}
                    onChange={(e) => handleColorChange(index, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    placeholder="#000000"
                    size="small"
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.85rem' }
                    }}
                  />
                  {hexColors.length > 1 && (
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRemoveColor(index) }} color="error" sx={{ flexShrink: 0 }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                  {pickingForIndex === index && (
                    <ColorLensIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} titleAccess="Picking from photo" />
                  )}
                </Box>
              ))}
              <Button startIcon={<AddIcon />} onClick={handleAddColor} size="small" sx={{ mt: 0.5 }}>
                Add another color
              </Button>

              {hexColors.length > 1 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">Preview:</Typography>
                  <ColorwayDot colors={hexColors} size={28} variant="pie" />
                  <ColorwayDot colors={hexColors} size={28} variant="stripes" />
                </Box>
              )}
            </Box>

            {/* Photo picker - right next to colors */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Pick from photo {hexColors.length > 1 && `(updating color ${pickingForIndex + 1})`}
              </Typography>
              <ColorPicker
                hexColor={hexColors[pickingForIndex]}
                onChange={(c) => handleColorChange(pickingForIndex, c)}
                minimal
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {onBack && (
          <Button onClick={onBack}>Back</Button>
        )}
        <Button onClick={onSubmit} variant="contained">{buttonText || 'Next: Add to Library'}</Button>
      </DialogActions>
    </Dialog>
  )
}
